import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserPlus, GraduationCap, BookOpen, Layers3,
  Presentation, CalendarClock, ClipboardCheck, Wallet, FileText,
  ClipboardList, FolderOpen, Bell, Building2, ShieldCheck, LogOut, PieChart,
  BadgeDollarSign, Home, Megaphone, Library, MonitorPlay,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV_ACCESS, ROLE_LABELS } from "@/lib/roles";

const NAV = [
  { key: "dashboard",     to: "/dashboard",     label: "Dashboard",       icon: LayoutDashboard },
  { key: "my_portal",     to: "/my-portal",     label: "My Portal",       icon: Home },
  { key: "exam_portal",   to: "/exam",          label: "Exam Portal",     icon: MonitorPlay },
  { key: "leads",         to: "/leads",         label: "Leads / CRM",     icon: PieChart },
  { key: "admissions",    to: "/admissions",    label: "Admissions",      icon: UserPlus },
  { key: "students",      to: "/students",      label: "Students",        icon: GraduationCap },
  { key: "courses",       to: "/courses",       label: "Courses",         icon: BookOpen },
  { key: "batches",       to: "/batches",       label: "Batches",         icon: Layers3 },
  { key: "faculty",       to: "/faculty",       label: "Faculty",         icon: Presentation },
  { key: "timetable",     to: "/timetable",     label: "Timetable",       icon: CalendarClock },
  { key: "attendance",    to: "/attendance",    label: "Attendance",      icon: ClipboardCheck },
  { key: "fees",          to: "/fees",          label: "Fees",            icon: Wallet },
  { key: "tests",         to: "/tests",         label: "Tests & Exams",   icon: FileText },
  { key: "exam_admin",    to: "/exam-admin",    label: "Exam Admin",      icon: MonitorPlay },
  { key: "question_bank", to: "/question-bank", label: "Question Bank",   icon: Library },
  { key: "assignments",   to: "/assignments",   label: "Assignments",     icon: ClipboardList },
  { key: "materials",     to: "/materials",     label: "Study Materials", icon: FolderOpen },
  { key: "notifications", to: "/notifications", label: "Notifications",   icon: Bell },
  { key: "broadcast",     to: "/broadcast",     label: "Broadcast",       icon: Megaphone },
  { key: "hr",            to: "/hr",            label: "HR & Payroll",    icon: Users },
  { key: "accounting",    to: "/accounting",    label: "Accounting",      icon: BadgeDollarSign },
  { key: "branches",      to: "/branches",      label: "Branches",        icon: Building2 },
  { key: "users",         to: "/users",         label: "Users & Roles",   icon: ShieldCheck },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const role = user?.role;
  const items = NAV.filter((n) => NAV_ACCESS[n.key]?.includes(role));

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 flex-shrink-0 hidden md:flex flex-col bg-white border-r border-[#E0DFDC] min-h-screen sticky top-0"
    >
      <div className="px-5 py-4 border-b border-[#E0DFDC]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#0A66C2] text-white flex items-center justify-center font-bold">C</div>
          <div>
            <div className="text-[15px] font-bold leading-tight text-[#000000E6]">CoachERP</div>
            <div className="text-[11px] text-[#00000099] leading-tight">Coaching Center Platform</div>
          </div>
        </div>
      </div>

      <div className="px-3 py-3 border-b border-[#E0DFDC]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#EAF3FB] flex items-center justify-center text-[#0A66C2] font-semibold">
            {user?.name?.[0] || "U"}
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[#000000E6] truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-[#00000099] truncate">{ROLE_LABELS[role] || role}</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.key}
              to={it.to}
              data-testid={`nav-${it.key}`}
              className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
            >
              <Icon size={17} strokeWidth={2} />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-[#E0DFDC]">
        <button
          data-testid="sidebar-logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="sb-link w-full"
        >
          <LogOut size={17} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
